/**
 * FileExtractor: handles the extract phase of the indexing pipeline.
 * Reads a file from disk, validates it, parses it with language/framework plugins,
 * and computes complexity metrics. Pure computation — no DB writes.
 */
import fs from 'node:fs';
import path from 'node:path';
import type { Store } from '../db/store.js';
import type { FileRow } from '../db/types.js';
import type { TraceMcpError } from '../errors.js';
import { logger } from '../logger.js';
import { executeFrameworkExtractNodes, executeLanguagePlugin } from '../plugin-api/executor.js';
import type { PluginRegistry } from '../plugin-api/registry.js';
import type {
  FileParseResult,
  FrameworkPlugin,
  ProjectContext,
  RawEdge,
} from '../plugin-api/types.js';
import { computeComplexity } from '../tools/analysis/complexity.js';
import type { GitignoreMatcher } from '../utils/gitignore.js';
import { hashContent } from '../utils/hasher.js';
import { isSqliteSidecarPath } from '../utils/db-family.js';
import { atomicWriteTargetPath, isHotChurnPath } from '../utils/hot-churn.js';
import {
  DEFAULT_MAX_FILE_SIZE,
  isBinaryBuffer,
  isSensitiveFile,
  validateFileSize,
  validatePath,
} from '../utils/security.js';
import type { ExtractResponse } from './extract-pool.js';
import type { WorkspaceInfo } from './monorepo.js';
import type { FileExtraction } from './pipeline-state.js';
import { buildProjectContext } from './project-context.js';

interface ExtractorContext {
  /**
   * Optional store. Only used as a fallback when `existingFiles` does not
   * already contain the row for the file being extracted. Workers run with
   * no store and supply `existing` directly via the per-call `opts`.
   */
  store?: Store;
  registry: PluginRegistry;
  rootPath: string;
  workspaces: WorkspaceInfo[];
  gitignore: GitignoreMatcher | undefined;
  fileContentCache: Map<string, string>;
  buildProjectContext: () => ProjectContext;
  /**
   * TRA-1543: workspace path → detected framework plugins, shared with the
   * pipeline's per-run memo (populated by registerFrameworkEdgeTypes before
   * extraction starts). When present, collectFrameworkExtracts skips its own
   * per-workspace detection round. Workers construct their own extractor
   * without it and keep the inline fallback below.
   */
  wsFrameworkPlugins?: Map<string, FrameworkPlugin[]>;
  /**
   * Pre-loaded existing file rows for the current pipeline run, indexed by
   * relative path. Lets `extract()` skip the per-file `store.getFile()` lookup
   * (which is the dominant DB hit during incremental reindex of a large set).
   */
  existingFiles?: Map<string, FileRow>;
  /**
   * Project-relative paths declared as `package.json#main`/`module`/`bin`/
   * `exports`. These are the package's public surface and must be indexed
   * even if they exceed the default file-size cap (e.g. lodash.js is 548 KB
   * declared as `main` — silently dropping it makes every published method
   * invisible to dead-code and call-graph queries). Mirrors jcodemunch
   * v1.80.9 force-include behavior.
   */
  forceIncludePaths?: ReadonlySet<string>;
}

/** Per-call inputs that override the context (used by the worker pool). */
export interface ExtractCallOptions {
  /** Pre-resolved existing FileRow (skips both the map and store lookups). */
  existing?: FileRow | null;
  /** Pre-resolved gitignore status (skips the matcher). */
  gitignored?: boolean;
}

/**
 * TRA-1768: per-process dedup for repeated identical language-plugin
 * failures. A broken environment (e.g. a smoke fixture's partial
 * `node_modules` missing the tree-sitter WASM grammars) fails EVERY file
 * of a language with the identical message — the old code logged one L50
 * per file (112 lines for a single cause) into the shared daemon.log.
 * Now the first occurrence keeps the full error log; repeats go to debug,
 * with a warn summary every N repeats. Keyed by root + error identity so
 * different projects — or genuinely different per-file errors — still log
 * independently. Mirrors the crash-dedup shape in extract-pool.ts.
 */
const PARSE_FAILURE_SUMMARY_EVERY = 50;
/** Bound on distinct signatures tracked; beyond it the map resets (DoS-safe). */
const MAX_PARSE_FAILURE_KEYS = 1000;
const parseFailureCounts = new Map<string, { count: number; firstFile: string }>();

function languagePluginFailureKey(rootPath: string, error: TraceMcpError): string {
  const message = 'message' in error ? error.message : error.code;
  return `${rootPath}\n${error.code}\n${message}`;
}

/** Test hook — clears the per-process dedup state between cases. */
export function resetLanguagePluginFailureDedupForTests(): void {
  parseFailureCounts.clear();
}

/**
 * TRA-1841 / TRA-2273: per-process dedup for the `File too large, skipping` and
 * binary skip logs. Expected skips for oversized and binary files must not pollute
 * daemon.log with level>=40 warnings (thousands of L40 entries for ML models,
 * datasets, icons, logs). Instead, log at debug (first occurrence includes path +
 * size/limit; repeats go to debug marked repeat suppressed), and account for them
 * in indexing skip counters so they are available via aggregated stats and diagnostics.
 */
const MAX_FILE_TOO_LARGE_KEYS = 1000;
const fileTooLargeLogged = new Set<string>();

function fileTooLargeKey(rootPath: string, relPath: string): string {
  return `${rootPath}\n${relPath}`;
}

const MAX_BINARY_KEYS = 1000;
const binaryLogged = new Set<string>();

function binaryKey(rootPath: string, relPath: string): string {
  return `${rootPath}\n${relPath}`;
}

/** Test hook — clears the per-process file-too-large and binary dedup state between cases. */
export function resetFileTooLargeWarnDedupForTests(): void {
  fileTooLargeLogged.clear();
  binaryLogged.clear();
}

/**
 * TRA-2326: per-process dedup and rate limiting for repeated identical
 * `Cannot read file` ENOENT warnings. When a disappearing file (such as
 * an atomic ML checkpoint or volatile scratchpad) is repeatedly triggered,
 * emitting hundreds of L40 warnings masks other signals and pollutes daemon.log.
 * The first ENOENT logs full warn; subsequent identical ENOENTs go to debug,
 * with a warn summary every N occurrences. Non-ENOENT read errors (e.g. EACCES)
 * always log warn without suppression.
 */
const CANNOT_READ_ENOENT_SUMMARY_EVERY = 50;
const MAX_CANNOT_READ_ENOENT_KEYS = 1000;
const cannotReadEnoentCounts = new Map<string, { count: number }>();

function cannotReadEnoentKey(rootPath: string, relPath: string): string {
  return `${rootPath}\n${atomicWriteTargetPath(relPath) ?? relPath}`;
}

/** Test hook — clears the per-process Cannot read file ENOENT dedup state between cases. */
export function resetCannotReadEnoentDedupForTests(): void {
  cannotReadEnoentCounts.clear();
}

function logCannotReadFile(
  rootPath: string,
  relPath: string,
  errno: NodeJS.ErrnoException | undefined,
  err: unknown,
): void {
  const code = errno?.code;
  const errorMsg = err instanceof Error ? err.message : String(err);

  // Preserve immediate warnings for non-ENOENT errors (EACCES, etc.)
  if (code !== 'ENOENT') {
    logger.warn(
      {
        file: relPath,
        rootPath,
        code,
        error: errorMsg,
      },
      'Cannot read file',
    );
    return;
  }

  const key = cannotReadEnoentKey(rootPath, relPath);
  const seen = cannotReadEnoentCounts.get(key);
  if (!seen) {
    if (cannotReadEnoentCounts.size >= MAX_CANNOT_READ_ENOENT_KEYS) cannotReadEnoentCounts.clear();
    cannotReadEnoentCounts.set(key, { count: 1 });
    logger.warn(
      {
        file: relPath,
        rootPath,
        code,
        error: errorMsg,
      },
      'Cannot read file',
    );
    return;
  }

  seen.count += 1;
  if (seen.count % CANNOT_READ_ENOENT_SUMMARY_EVERY === 0) {
    logger.warn(
      {
        file: relPath,
        rootPath,
        code,
        repeatCount: seen.count,
        error: errorMsg,
      },
      `Cannot read file ${seen.count} times with ENOENT (summary, details at debug)`,
    );
    return;
  }

  logger.debug(
    {
      file: relPath,
      rootPath,
      code,
      repeatCount: seen.count,
    },
    'Cannot read file (repeat suppressed)',
  );
}

function logFileTooLargeOnce(
  rootPath: string,
  relPath: string,
  size: number,
  limit: number,
  message = 'File too large, skipping',
): void {
  const key = fileTooLargeKey(rootPath, relPath);
  if (fileTooLargeLogged.has(key)) {
    logger.debug({ file: relPath, rootPath, size }, `${message} (repeat suppressed)`);
    return;
  }
  if (fileTooLargeLogged.size >= MAX_FILE_TOO_LARGE_KEYS) fileTooLargeLogged.clear();
  fileTooLargeLogged.add(key);
  logger.debug({ file: relPath, rootPath, size, limit }, message);
}

function logBinaryOnce(rootPath: string, relPath: string): void {
  const key = binaryKey(rootPath, relPath);
  if (binaryLogged.has(key)) {
    logger.debug({ file: relPath, rootPath }, 'Binary file detected, skipping (repeat suppressed)');
    return;
  }
  if (binaryLogged.size >= MAX_BINARY_KEYS) binaryLogged.clear();
  binaryLogged.add(key);
  logger.debug({ file: relPath, rootPath }, 'Binary file detected, skipping');
}

/**
 * TRA-1807: process-once tripwire for a deleted install tree. When the
 * daemon's own `tree-sitter-wasm/out` directory is gone (e.g. a foreign
 * postinstall kickstarted the live daemon from an agent sandbox that has
 * since been deleted), EVERY parse of EVERY language fails with the same
 * ENOENT — a config-level fatal, not a per-file problem. Log it once with
 * the running entry + remediation; everything after goes to debug with no
 * counters. (Each worker thread carries its own copy of this flag, so a
 * 4-worker pool emits at most one line per worker.)
 */
let warnedMissingWasmEnv = false;

/** Test hook — clears the TRA-1807 missing-WASM tripwire between cases. */
export function resetMissingWasmEnvWarnForTests(): void {
  warnedMissingWasmEnv = false;
}

function isMissingWasmEnvError(error: TraceMcpError): boolean {
  const message = 'message' in error ? String(error.message) : String(error.code);
  const code = String(error.code ?? '');
  if (!/ENOENT/i.test(code) && !/ENOENT/i.test(message)) return false;
  return message.includes('tree-sitter-wasm') || message.includes('.wasm');
}

function logMissingWasmEnvOnce(rootPath: string, relPath: string, error: TraceMcpError): boolean {
  if (!isMissingWasmEnvError(error)) return false;
  if (warnedMissingWasmEnv) {
    logger.debug(
      { file: relPath, rootPath, code: error.code },
      'Language plugin failed (missing WASM env, already reported)',
    );
    return true;
  }
  warnedMissingWasmEnv = true;
  logger.error(
    {
      file: relPath,
      rootPath,
      code: error.code,
      error,
      entry: process.argv[1] ?? null,
    },
    'Language plugin failed: tree-sitter WASM grammars unreadable (ENOENT) — ' +
      'the install tree this process runs from is likely deleted (TRA-1807). ' +
      'Restart the daemon from a stable install; per-file repeats suppressed after this line',
  );
  return true;
}

function logLanguagePluginFailure(rootPath: string, relPath: string, error: TraceMcpError): void {
  // Config-fatal first: a missing install tree must not feed the per-root
  // counters (they reached 4750+ summaries in the TRA-1807 incident).
  if (logMissingWasmEnvOnce(rootPath, relPath, error)) return;
  const key = languagePluginFailureKey(rootPath, error);
  const seen = parseFailureCounts.get(key);
  if (!seen) {
    if (parseFailureCounts.size >= MAX_PARSE_FAILURE_KEYS) parseFailureCounts.clear();
    parseFailureCounts.set(key, { count: 1, firstFile: relPath });
    logger.error({ file: relPath, rootPath, code: error.code, error }, 'Language plugin failed');
    return;
  }
  seen.count += 1;
  if (seen.count % PARSE_FAILURE_SUMMARY_EVERY === 0) {
    logger.warn(
      {
        rootPath,
        code: error.code,
        repeatCount: seen.count,
        firstFile: seen.firstFile,
        error,
      },
      `Language plugin failed ${seen.count} times with same error (summary, per-file details at debug)`,
    );
    return;
  }
  logger.debug(
    { file: relPath, rootPath, code: error.code },
    'Language plugin failed (repeat suppressed)',
  );
}

export class FileExtractor {
  constructor(private ctx: ExtractorContext) {}

  async extract(
    relPath: string,
    force: boolean,
    opts: ExtractCallOptions = {},
  ): Promise<ExtractResponse> {
    const { registry, rootPath } = this.ctx;
    const absPath = path.resolve(rootPath, relPath);

    // Defence-in-depth: reject paths that escape the project root
    const pathCheck = validatePath(relPath, rootPath);
    if (pathCheck.isErr()) {
      logger.warn({ file: relPath }, 'Path traversal blocked');
      return { kind: 'error' };
    }

    // TRA-1943: SQLite WAL/SHM/journal sidecars are engine scratch, not
    // source — and they blink in and out as the engine checkpoints, so even
    // stating them is a race. Drop before any filesystem touch.
    if (isSqliteSidecarPath(relPath)) {
      logger.debug({ file: relPath }, 'SQLite sidecar skipped (not source)');
      return { kind: 'skipped' };
    }

    // TRA-2021: hot-churn runtime state (`gateway.heartbeat`,
    // `cron/ticker_*`, `cron/.tick.lock`, plus the TRA-2031 atomic tmp
    // siblings) — rewritten every ~30 s, never
    // source. The entry points above drop it before the extractor ever
    // runs; this is the safety net for direct callers.
    if (isHotChurnPath(relPath)) {
      logger.debug({ file: relPath }, 'Hot-churn runtime state skipped (not source)');
      return { kind: 'skipped' };
    }

    // Reject symlinks to prevent escaping the project root
    let fileMtimeMs: number | null = null;
    let fileSize: number | null = null;
    try {
      const stat = fs.lstatSync(absPath);
      if (stat.isSymbolicLink()) {
        logger.warn({ file: relPath }, 'Symlink skipped');
        return { kind: 'error' };
      }
      // TRA-1649: the watcher enqueues directory paths (mkdir/create events
      // for `.multica`, `.opencode/skills/...`, etc.). readFileSync below
      // throws EISDIR on them, which was counted as an indexing `error` and
      // retried forever — a directory never becomes a file. Drop them here
      // as `skipped`, before any read.
      if (stat.isDirectory()) {
        logger.debug({ file: relPath }, 'Directory skipped (not a file)');
        return { kind: 'skipped' };
      }
      fileMtimeMs = stat.mtimeMs;
      // TRA-1536: captured here so the size gates below need no second stat —
      // the pre-read size precheck and the hardened mtime fast-path both read
      // it for free.
      fileSize = stat.size;
    } catch {
      // lstat failed — file may not exist; readFileSync below will catch it
    }

    // Block sensitive files (credentials, keys, secrets) from indexing
    if (isSensitiveFile(relPath)) {
      logger.warn({ file: relPath }, 'Sensitive file blocked from indexing');
      return { kind: 'skipped' };
    }

    // Existing-row lookup. Resolution order:
    //   1. caller-supplied via opts.existing (worker path)
    //   2. context preload Map (CLI path; one IN-query upfront)
    //   3. store.getFile fallback (single-row SELECT)
    const existing =
      opts.existing ?? this.ctx.existingFiles?.get(relPath) ?? this.ctx.store?.getFile(relPath);

    // mtime fast-path: if mtime hasn't changed, the file content is identical —
    // skip the expensive read + hash computation entirely.
    // TRA-1536: size-hardened. A rewrite inside the same mtime-ms floor with
    // a different byte length must NOT skip — the old mtime-only check
    // missed exactly that (fast append + immediate reindex). Legacy rows
    // without a stored byte_length keep the mtime-only verdict.
    if (
      !force &&
      fileMtimeMs != null &&
      existing &&
      existing.mtime_ms != null &&
      existing.mtime_ms === Math.floor(fileMtimeMs) &&
      (fileSize == null || existing.byte_length == null || existing.byte_length === fileSize)
    ) {
      return { kind: 'skipped' };
    }

    // TRA-1536: stat-size precheck BEFORE readFileSync. The old code read the
    // whole file into memory and only then asked validateFileSize — every
    // >1 MB binary/artifact paid a full read + Buffer alloc just to be
    // rejected. The lstat above already told us the size for free. The
    // post-read check stays as a TOCTOU guard (file grown between stat and
    // read); package entry points keep their 5 MB ceiling either way.
    const isForceIncluded = this.ctx.forceIncludePaths?.has(relPath) ?? false;
    if (fileSize != null) {
      const preCheck = validateFileSize(fileSize, isForceIncluded ? 5 * 1024 * 1024 : undefined);
      if (preCheck.isErr()) {
        logFileTooLargeOnce(
          rootPath,
          relPath,
          fileSize,
          isForceIncluded ? 5 * 1024 * 1024 : DEFAULT_MAX_FILE_SIZE,
        );
        return { kind: 'skipped', reason: 'oversize' };
      }
    }

    let content: Buffer;
    try {
      content = fs.readFileSync(absPath);
    } catch (err) {
      // TRA-1649 TOCTOU guard: the path was a file at lstat time but became
      // a directory before the read (or lstat failed above and the read is
      // the first touch). EISDIR is a directory, not a failure — skip it.
      if ((err as NodeJS.ErrnoException)?.code === 'EISDIR') {
        logger.debug({ file: relPath }, 'Directory skipped (not a file)');
        return { kind: 'skipped' };
      }
      const errno = err as NodeJS.ErrnoException;
      // TRA-1943: a sidecar that slipped past the pre-stat gate (stale
      // watcher event arriving after the engine unlinked it) died in the
      // readdir→open race — expected engine churn, not an indexing failure.
      // Debug, and `skipped` so it neither counts as an error nor retries.
      if (errno?.code === 'ENOENT' && isSqliteSidecarPath(relPath)) {
        logger.debug(
          { file: relPath, rootPath, code: errno?.code },
          'SQLite sidecar vanished before read (race, not an error)',
        );
        return { kind: 'skipped' };
      }
      // TRA-1715: the bare `{ file }` record was undiagnosable — 3317 of them
      // with no root and no cause. Carry the root plus the errno code and
      // message (ENOENT vs EACCES decides the response) so the daemon log
      // alone answers which project lost which file and why.
      // TRA-2326: rate-limit repeated identical ENOENTs for vanishing files
      // so ML checkpoint races or scratch files do not flood daemon.log.
      logCannotReadFile(rootPath, relPath, errno, err);
      return { kind: 'error' };
    }

    // Reject binary files (null-byte in first 8 KB)
    if (isBinaryBuffer(content)) {
      logBinaryOnce(rootPath, relPath);
      return { kind: 'skipped', reason: 'binary' };
    }

    // Post-read size gate (TOCTOU companion of the stat precheck above):
    // reject oversized files (default 1 MB) to prevent OOM — UNLESS the
    // file is declared as a package entry point (main/module/bin/exports).
    // Public API surface must be indexed regardless of monolithic size,
    // otherwise dead-code/call-graph results are systematically wrong for
    // single-file libraries (lodash-class).
    if (!isForceIncluded) {
      const sizeCheck = validateFileSize(content.length);
      if (sizeCheck.isErr()) {
        logFileTooLargeOnce(rootPath, relPath, content.length, DEFAULT_MAX_FILE_SIZE);
        return { kind: 'skipped', reason: 'oversize' };
      }
    } else if (content.length > 5 * 1024 * 1024) {
      // Above 5MB even a declared entry point is more likely a bundled
      // artifact than real source. Refuse and let the operator opt in
      // via an explicit raise of validateFileSize's max if they know.
      logFileTooLargeOnce(
        rootPath,
        relPath,
        content.length,
        5 * 1024 * 1024,
        'force-included package entry exceeds 5 MB hard ceiling — skipping',
      );
      return { kind: 'skipped', reason: 'oversize' };
    }

    // mtime drifted but content might be identical (formatter-on-save, git
    // checkout that touches mtime). Compare content_hash first — cheap
    // xxh64 hash beats a full reparse. Persist the new mtime so subsequent
    // runs hit the free mtime fast-path above.
    const hash = hashContent(content);
    if (!force && existing && existing.content_hash === hash) {
      const newMtime = fileMtimeMs != null ? Math.floor(fileMtimeMs) : null;
      if (newMtime != null && existing.mtime_ms !== newMtime) {
        if (this.ctx.store) {
          // In-process path: write directly.
          this.ctx.store.updateFileMtime(existing.id, newMtime);
          return { kind: 'skipped' };
        }
        // WHY: workers receive a fixture context with no DB handle — surface
        // the update so the main thread can persist it. Without this the
        // mtime fast-path never re-arms after a hash-hit in the worker path.
        return { kind: 'mtime_updated', fileId: existing.id, newMtimeMs: newMtime };
      }
      return { kind: 'skipped' };
    }

    // Cache content for Pass 2 (resolveEdges reads files again)
    const contentStr = content.toString('utf-8');
    this.ctx.fileContentCache.set(relPath, contentStr);

    // Find matching language plugin. Pass the file's first bytes so the
    // registry can fall back to a `#!` shebang when the extension match
    // fails — extensionless scripts like bin/deploy or scripts/migrate
    // would otherwise drop out of the index entirely.
    const plugin = registry.getLanguagePluginForFileWithFallback(relPath, content);
    if (!plugin) {
      return { kind: 'skipped' };
    }

    // Execute language plugin. The tree-cache scope (this run's project root)
    // namespaces per-file tree-sitter entries so co-hosted projects sharing
    // a relPath don't alias into one cache slot (see ExtractSymbolsOptions).
    const parseResult = await executeLanguagePlugin(plugin, relPath, content, undefined, {
      treeCacheScope: rootPath,
    });
    if (parseResult.isErr()) {
      logLanguagePluginFailure(rootPath, relPath, parseResult.error);
      return { kind: 'error' };
    }

    const parsed = parseResult.value;
    const language = parsed.language ?? this.detectLanguage(relPath);
    const workspace = this.resolveWorkspacePath(relPath);

    // Compute complexity metrics and attach to symbol metadata.
    this.computeSymbolMetrics(parsed.symbols, contentStr, language);

    // Separate import edges from other edges
    const otherEdges: RawEdge[] = [];
    const importEdges: { from: string; specifiers: string[]; relPath: string }[] = [];
    if (parsed.edges?.length) {
      for (const edge of parsed.edges) {
        // Capture both JS/TS imports and Python imports for file-level resolution
        const isImportEdge =
          (edge.edgeType === 'imports' ||
            edge.edgeType === 'py_imports' ||
            edge.edgeType === 'php_imports') &&
          !edge.sourceNodeType &&
          !edge.sourceSymbolId;
        if (isImportEdge) {
          const meta = edge.metadata as Record<string, unknown> | undefined;
          // Plugins are split on the metadata key: the JS/TS/Python/Ruby family
          // writes `from`, the compiled-language family (Go, Java, C#, Kotlin,
          // C/C++) writes `module`. Reading only `from` left the latter with an
          // empty specifier, which every downstream resolver drops (TRA-449).
          importEdges.push({
            from: ((meta?.from ?? meta?.module) as string) ?? '',
            specifiers: (meta?.specifiers as string[]) ?? [],
            relPath,
          });
        } else {
          otherEdges.push(edge);
        }
      }
    }

    // Collect framework extract results (no DB writes). Awaits async
    // plugins (e.g. ReactPlugin uses tree-sitter via async getParser).
    // TRA-1537: pass the already-decoded `contentStr` — one utf-8 decode per
    // file shared across all framework plugins instead of N per-plugin
    // decodes. The executor forwards it as-is; plugins calling
    // `content.toString('utf-8')` get the same string back with no work.
    const frameworkExtracts = await this.collectFrameworkExtracts(relPath, contentStr, language);

    const extraction: FileExtraction = {
      relPath,
      existingId: existing?.id ?? null,
      hash,
      contentSize: content.length,
      language,
      workspace,
      gitignored: opts.gitignored ?? this.ctx.gitignore?.isIgnored(relPath) ?? false,
      status: parsed.status,
      frameworkRole: parsed.frameworkRole,
      mtimeMs: fileMtimeMs != null ? Math.floor(fileMtimeMs) : null,
      symbols: parsed.symbols,
      otherEdges,
      importEdges,
      routes: parsed.routes ?? [],
      components: parsed.components ?? [],
      migrations: parsed.migrations ?? [],
      ormModels: parsed.ormModels ?? [],
      ormAssociations: parsed.ormAssociations ?? [],
      rnScreens: parsed.rnScreens ?? [],
      frameworkExtracts,
    };
    return { kind: 'ok', extraction };
  }

  private computeSymbolMetrics(
    symbols: FileExtraction['symbols'],
    contentStr: string,
    language: string,
  ): void {
    for (const sym of symbols) {
      if (sym.kind === 'function' || sym.kind === 'method' || sym.kind === 'class') {
        const lines = (sym.lineEnd ?? sym.lineStart ?? 0) - (sym.lineStart ?? 0);
        if (lines <= 2) {
          sym.metadata = {
            ...(sym.metadata ?? {}),
            cyclomatic: 1,
            max_nesting: 0,
            param_count: sym.signature
              ? computeComplexity('', sym.signature, language).param_count
              : 0,
          };
          continue;
        }
        const source = contentStr.slice(sym.byteStart, sym.byteEnd);
        const metrics = computeComplexity(source, sym.signature, language);
        sym.metadata = {
          ...(sym.metadata ?? {}),
          cyclomatic: metrics.cyclomatic,
          max_nesting: metrics.max_nesting,
          param_count: metrics.param_count,
        };
      }
    }
  }

  /** Cache: workspace path → detected framework plugins */
  private wsPluginCache = new Map<string, FrameworkPlugin[]>();
  /** Cache: root-level active framework plugins (computed once per extractor). */
  private rootPluginCache: FrameworkPlugin[] | undefined;

  /**
   * TRA-1537 §1 — per-file detect overhead is already zero by construction:
   * workspace detection runs once per workspace path (`wsPluginCache`) and
   * root detection once per extractor (`rootPluginCache`); the per-file path
   * is two `Map.get` lookups, no `buildProjectContext()` / `detect()` calls.
   * Only plugins whose `detect()` fired for this workspace/root are invoked
   * below — there is no per-file scan over all 87 registered integrations.
   *
   * `content` is the file already decoded to string by `extract()` and shared
   * across every plugin invocation for this file (one utf-8 decode total).
   */
  private async collectFrameworkExtracts(
    relPath: string,
    content: Buffer | string,
    language: string,
  ): Promise<FileParseResult[]> {
    // Determine which plugins to run and what path to pass.
    // In a monorepo, each workspace may have its own framework (e.g. fair-front = Nuxt,
    // fair-laravel = Laravel).  We need to:
    // 1. Detect frameworks per-workspace (not just root)
    // 2. Pass workspace-relative paths to extractNodes so path prefixes match

    let plugins: FrameworkPlugin[] = [];
    let extractPath = relPath; // path passed to extractNodes

    // Try workspace-level detection first
    const wsPath = this.resolveWorkspacePath(relPath);
    if (wsPath) {
      // TRA-1543: prefer the pipeline's per-run detection (already computed
      // for edge-type registration). The local cache + inline detect below
      // remain for worker-constructed extractors that never received it.
      const shared = this.ctx.wsFrameworkPlugins?.get(wsPath);
      if (shared !== undefined) {
        if (shared.length > 0) {
          plugins = shared;
          extractPath = relPath.slice(wsPath.length + 1);
        }
      } else {
        let cached = this.wsPluginCache.get(wsPath);
        if (cached === undefined) {
          const wsRoot = path.join(this.ctx.rootPath, wsPath);
          const wsCtx = buildProjectContext(wsRoot);
          cached = this.ctx.registry.getAllFrameworkPlugins().filter((p) => p.detect(wsCtx));
          this.wsPluginCache.set(wsPath, cached);
        }
        if (cached.length > 0) {
          plugins = cached;
          // Strip workspace prefix so NuxtPlugin sees "app/pages/index.vue" not "fair/fair-front/app/pages/index.vue"
          extractPath = relPath.slice(wsPath.length + 1);
        }
      }
    }

    // Fallback to root-level plugins. Cached: project context + active plugin
    // list don't change mid-run, so do this work once instead of per-file.
    if (plugins.length === 0) {
      if (this.rootPluginCache === undefined) {
        const ctx = this.ctx.buildProjectContext();
        const activeResult = this.ctx.registry.getActiveFrameworkPlugins(ctx);
        this.rootPluginCache = activeResult.isOk() ? activeResult.value : [];
      }
      plugins = this.rootPluginCache;
    }

    if (plugins.length === 0) return [];

    const results: FileParseResult[] = [];
    for (const plugin of plugins) {
      if (!plugin.extractNodes) continue;
      // Most plugins are sync (regex-only); the few that need async parser
      // init (e.g. ReactPlugin via tree-sitter getParser) hit the await
      // branch inside the executor. The outer extract() call already
      // provides timeout/error containment.
      const result = await executeFrameworkExtractNodes(plugin, extractPath, content, language);
      if (result.isErr() || !result.value) continue;
      results.push(result.value);
    }
    return results;
  }

  /** Returns the workspace path (relative to root) that contains `relPath`, or null. */
  private resolveWorkspacePath(relPath: string): string | null {
    for (const ws of this.ctx.workspaces) {
      if (relPath.startsWith(`${ws.path}/`) || relPath === ws.path) {
        return ws.path;
      }
    }
    return null;
  }

  private detectLanguage(filePath: string): string {
    const ext = path.extname(filePath).slice(1);
    const map: Record<string, string> = {
      php: 'php',
      ts: 'typescript',
      tsx: 'typescript',
      js: 'javascript',
      jsx: 'javascript',
      mjs: 'javascript',
      mts: 'typescript',
      vue: 'vue',
    };
    return map[ext] ?? ext;
  }
}
